import React, { memo, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import LoadingSpinner from '../LoadingSpinner';

// Design tokens for buttons
export const buttonVariants = {
  primary: css`
    background: var(--color-primary);
    color: var(--color-gray-800);
    border: 2px solid var(--color-primary);
    
    &:hover:not(:disabled) {
      background: #8BB5E0;
      border-color: #8BB5E0;
    }
    
    &:active:not(:disabled) {
      background: #7BA5D0;
      border-color: #7BA5D0;
    }
  `,
  secondary: css`
    background: var(--color-white);
    color: var(--color-gray-700);
    border: 2px solid var(--color-gray-300);
    
    &:hover:not(:disabled) {
      background: var(--color-gray-50);
      border-color: var(--color-gray-400);
    }
    
    &:active:not(:disabled) {
      background: var(--color-gray-100);
    }
  `,
  success: css`
    background: var(--color-green-600);
    color: var(--color-white);
    border: 2px solid var(--color-green-600);
    
    &:hover:not(:disabled) {
      background: var(--color-green-700);
      border-color: var(--color-green-700);
    }
  `,
  danger: css`
    background: var(--color-red-600);
    color: var(--color-white);
    border: 2px solid var(--color-red-600);
    
    &:hover:not(:disabled) {
      background: var(--color-red-700);
      border-color: var(--color-red-700);
    }
  `,
  ghost: css`
    background: transparent;
    color: var(--color-gray-700);
    border: 2px solid transparent;
    
    &:hover:not(:disabled) {
      background: var(--color-gray-100);
    }
  `
} as const;

export const buttonSizes = {
  sm: css`
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
    border-radius: var(--border-radius-md);
  `,
  md: css`
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    border-radius: var(--border-radius-md);
  `,
  lg: css`
    padding: 1rem 2rem;
    font-size: 1.125rem;
    border-radius: var(--border-radius-lg);
  `
} as const;

interface ButtonProps {
  children: React.ReactNode;
  variant?: keyof typeof buttonVariants;
  size?: keyof typeof buttonSizes;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  className?: string;
  'data-testid'?: string;
  'aria-label'?: string;
}

const StyledButton = styled.button<{
  $variant: keyof typeof buttonVariants;
  $size: keyof typeof buttonSizes;
  $fullWidth: boolean;
  $loading: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: var(--font-sans);
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  position: relative;
  
  ${({ $variant }) => buttonVariants[$variant]}
  ${({ $size }) => buttonSizes[$size]}
  
  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &:focus {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
  
  ${({ $loading }) =>
    $loading &&
    css`
      color: transparent;
    `}
`;

const LoadingWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

/**
 * Reusable Button component with consistent styling and accessibility
 * 
 * @example
 * <Button variant="primary" size="md" loading={isSubmitting} onClick={handleSubmit}>
 *   Submit Form
 * </Button>
 */
export const Button = memo(
  forwardRef<HTMLButtonElement, ButtonProps>(({
    children,
    variant = 'primary',
    size = 'md',
    loading = false,
    disabled = false,
    fullWidth = false,
    type = 'button',
    onClick,
    className,
    'data-testid': testId,
    'aria-label': ariaLabel
  }, ref) => {
    const isDisabled = disabled || loading;

    return (
      <StyledButton
        ref={ref}
        type={type}
        className={className}
        $variant={variant}
        $size={size}
        $fullWidth={fullWidth}
        $loading={loading}
        disabled={isDisabled}
        onClick={onClick}
        data-testid={testId}
        aria-label={ariaLabel}
        aria-disabled={isDisabled}
      >
        {loading && (
          <LoadingWrapper>
            <LoadingSpinner size="small" />
          </LoadingWrapper>
        )}
        {children}
      </StyledButton>
    );
  })
);

Button.displayName = 'Button';